import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EmployeeDetailsResponse,
  EmployeeHSAStatusChanges,
  PlanPeriodDto,
  ProductIds,
  benefitPlanPeriods,
  getEmployeeDetails,
  getHSAStatus,
  updateHSAStatus,
} from 'core'

import { employeeHSAColumns } from 'common/components/employeeProfile/employeeHSA/EmployeeHSAstatus'

import { Card, Notification, Table } from 'common/components'
import { formatYear } from 'common/dateFormatters'
import { useLoginUserV2, useQueryRequestV2 } from 'common/hooks'

import HSAModal from './HSAModal'
import HSASuccessModal from './HSASuccessModal'

const defaultFilter = {
  pageNumber: 1,
  pageSize: 10,
  orderColumn: 'lastModifiedAt',
  orderDescending: false,
  searchValue: null,
}

export type ExpenseTotals = {
  expenseCount: number
  totalAmount: number
}

const HSAStatus = () => {
  const { t: translate } = useTranslation()

  const { user } = useLoginUserV2()
  const employeeId = user.companyUserId

  const { data: employeeDetails } = useQueryRequestV2<EmployeeDetailsResponse>({
    initialState: null,
    queryFn: () => getEmployeeDetails(employeeId),
  })

  const [planPeriods, setPlanPeriods] = useState<PlanPeriodDto[] | undefined>(undefined)
  const [loadingEmployeeHSAStatusData, setLoadingEmployeeHSAStatusData] = useState<boolean>(false)
  const [isHSAModalOpen, setIsHSAModalOpen] = useState<boolean>(false)
  const [isHSASuccessUpdateModalOpen, setIsHSASuccessUpdateModalOpen] = useState<boolean>(false)
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto>(null)
  const hasMultiplePlanPeriod = user.productId === ProductIds.GroupHraId
  const [expenseTotals, setExpenseTotals] = useState<ExpenseTotals>(null)

  const initializeValues = async (benefitPlanPeriod: any) => {
    for (let j = 0; j < benefitPlanPeriod.length; j++) {
      var startDate = parseInt(formatYear(new Date(benefitPlanPeriod[j].startDate)), 10)
      var endDate = parseInt(formatYear(new Date(benefitPlanPeriod[j].endDate)), 10)
      benefitPlanPeriod[j].yearStartDate = startDate
      benefitPlanPeriod[j].yearEndDate = endDate
      benefitPlanPeriod[j].yearMatchStartDate = 'N/A'
      benefitPlanPeriod[j].yearMatchEndDate = 'N/A'
    }

    return benefitPlanPeriod
  }

  const updateEmployeeHSATable = async () => {
    setLoadingEmployeeHSAStatusData(true)
    const result = await getHSAStatus(defaultFilter)
    const benefitPlanPeriod = await benefitPlanPeriods()
    const benefitPlanPeriodInitialized = await initializeValues(benefitPlanPeriod)

    if (benefitPlanPeriodInitialized !== undefined) {
      for (let i = 0; i < result.items.length; i++) {
        let hsaStatus = result.items[i].contributesToHSA
        for (let j = 0; j < benefitPlanPeriod.length; j++) {
          if (result.items[i].year === benefitPlanPeriodInitialized[j].yearStartDate) {
            if (hsaStatus) {
              benefitPlanPeriodInitialized[j].yearMatchStartDate = 'Yes'
            } else {
              benefitPlanPeriodInitialized[j].yearMatchStartDate = 'No'
            }
          }
          if (result.items[i].year === benefitPlanPeriodInitialized[j].yearEndDate) {
            if (hsaStatus) {
              benefitPlanPeriodInitialized[j].yearMatchEndDate = 'Yes'
            } else {
              benefitPlanPeriodInitialized[j].yearMatchEndDate = 'No'
            }
          }
        }
      }
    }

    setPlanPeriods(benefitPlanPeriodInitialized)
    setLoadingEmployeeHSAStatusData(false)
  }

  const handleHSAModalOpen = (selectedPlan: PlanPeriodDto) => {
    setIsHSAModalOpen(true)
    setSelectedPlanPeriod(selectedPlan)
  }

  const handleHSAModalClose = () => {
    setIsHSAModalOpen(false)
  }

  const handleSuccessModalClose = () => {
    setIsHSASuccessUpdateModalOpen(false)
    updateEmployeeHSATable()
  }

  const submitHSAChanges = async (yearDropDownValue: string, hsaRadioOptionValue: string) => {
    let year = yearDropDownValue
    if (
      selectedPlanPeriod !== null &&
      formatYear(new Date(selectedPlanPeriod.startDate)) === formatYear(new Date(selectedPlanPeriod.endDate))
    ) {
      year = formatYear(new Date(selectedPlanPeriod.startDate))
    }
    let editHSAData: EmployeeHSAStatusChanges = {
      contributesToHSA: hsaRadioOptionValue === 'y',
      year: hasMultiplePlanPeriod ? Number(year) : Number(formatYear(new Date(selectedPlanPeriod?.endDate))),
    }

    const result = await updateHSAStatus(editHSAData)

    if (result) {
      Notification.open({
        message: translate('employee-profile.update-success-message'),
        type: 'success',
      })
      handleHSAModalClose()
      if (hsaRadioOptionValue === 'y') {
        setIsHSASuccessUpdateModalOpen(true)
        setExpenseTotals({
          expenseCount: result.expenseCount,
          totalAmount: result.totalAmount,
        })
      } else {
        updateEmployeeHSATable()
      }
    } else {
      Notification.open({
        message: 'Error saving HSA status',
        type: 'error',
      })
    }
  }

  useEffect(() => {
    if (employeeDetails) {
      updateEmployeeHSATable()
    }
  }, [employeeDetails])

  return (
    <>
      <Card title={translate('admin-dashboard.manage-people.view-employee.benefit-history.columns.hsa-title')}>
        <Table
          dataSource={planPeriods}
          loading={loadingEmployeeHSAStatusData}
          columns={employeeHSAColumns(translate, defaultFilter, handleHSAModalOpen, hasMultiplePlanPeriod)}
          pagination={false}
        />
      </Card>
      {isHSAModalOpen && selectedPlanPeriod !== null && (
        <HSAModal
          isHSAModalOpen={isHSAModalOpen}
          onHSAModalClose={handleHSAModalClose}
          hasMultiplePlanPeriod={hasMultiplePlanPeriod}
          submitHSAChanges={submitHSAChanges}
          selectedPlanPeriod={selectedPlanPeriod}
        />
      )}
      {isHSASuccessUpdateModalOpen && (
        <HSASuccessModal expenseTotals={expenseTotals} handleSuccessModalClose={handleSuccessModalClose} />
      )}
    </>
  )
}

export default HSAStatus
